import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { camera } from './scene.js';
import { spawnSparks, addDecal, spawnTracer, spawnMuzzleSmoke, spawnBloodSplat } from './fx.js';
import { playShot, playReload, stopReload, playSwitch } from './audio.js';
import { exitNade } from './grenades.js';
import { sendFire, sendReload, sendSwitch, spreadOffset, spreadScale, me } from './net.js';
import { bodiesAlong } from './remote.js';
import { rayWalls } from './player.js';
import { player, S, clock, wallMeshes, BASE_FOV, aimDir, _wp } from './state.js';

/** Индексы оружия в протоколе — зеркало shared/src/weapons.rs::WeaponId */
export const WEAPON_ID = { pistol: 0, rifle: 1, sniper: 2 };
export const WEAPON_KEY = ['pistol', 'rifle', 'sniper'];

// ============================================================
//  Оружие: три ствола, вьюмодели, стрельба, перезарядка.
// ============================================================

export const WEAPONS = {
  pistol: { name: 'Пистолет', dmg: 34, headMul: 2.5, rof: 0.20, mag: 12, reload: 1.1, spread: 0.010, recoil: 0.020, adsFov: 58, auto: false, scope: false },
  rifle: { name: 'Винтовка', dmg: 26, headMul: 3.0, rof: 0.10, mag: 30, reload: 1.5, spread: 0.012, recoil: 0.013, adsFov: 50, auto: true, scope: false },
  sniper: { name: 'Снайперка', dmg: 130, headMul: 1.6, rof: 1.05, mag: 5, reload: 2.4, spread: 0.001, recoil: 0.060, adsFov: 18, auto: false, scope: true },
};
// lastShot — на каждый ствол свой, иначе кулдаун переносится между стволами
// и первый выстрел после смены оружия «не проходит».
Object.values(WEAPONS).forEach(w => { w.ammo = w.mag; w.lastShot = -10; });

export const W = () => WEAPONS[S.curKey];

// Вьюмодели — крепим к камере, переключаем видимость.
// Часть стволов — GLB-модели (CC0-пак Quaternius, см. md/graphics.md),
// часть — примитивы-заглушки. Замену ведём по стволам, поэтому одна функция
// умеет и то, и другое.
export const gunHolder = new THREE.Group(); camera.add(gunHolder);
const metalDark = new THREE.MeshStandardMaterial({ color: 0x2b2f36, roughness: 0.6, metalness: 0.4 });
const metalBlack = new THREE.MeshStandardMaterial({ color: 0x15181d, roughness: 0.4, metalness: 0.6 });

// ---- GLB-вьюмодели ----
//
// Файлы лежат в client/public/models/ и попадают в dist/ как /models/*.glb.
// Лицензия CC0 (Quaternius, Ultimate Gun Pack) — можно коммерчески без атрибуции.
//
// ⚠️ У пака ось ствола = +X, длина модели ~3.43 ед., дуло в точке (3.247, 0.655, 0).
// Подгонка (`FIT`) переводит её в систему вьюмодели: forward = −Z, up = +Y.
//   scale — привести длину к ~0.6 м;
//   rotY  — π/2 разворачивает +X (дуло) в −Z (от игрока);
//   offset — доводка положения на глаз (модель центрируется по bbox, потом сдвиг);
//   muzzle — точка дула В КООРДИНАТАХ МОДЕЛИ; из неё считаем позицию вспышки
//            ТОЧНО (той же матрицей), а не на глаз — на неё завязаны трассер,
//            поджим у стены и поджим у пола (см. tryShoot / updateWeaponView).
//   tint   — перекраска частей по имени материала: OBJ-экспорт Quaternius теряет
//            палитру и красит всё почти в чёрное, поэтому назначаем цвета сами.
const GUN_MODELS = {
  pistol: {
    url: '/models/pistol.glb',
    scale: 0.155,
    rotY: Math.PI / 2,
    offset: [0.0, -0.02, -0.04],
    muzzle: [1.471, 0.551, 0],
    tint: { Wood: 0x6b4a2b, Metal: 0x3a3f47, Black: 0x141518, LightMetal: 0x6a7078 },
    // Точки хвата В КООРДИНАТАХ МОДЕЛИ (как muzzle): r — правая рука на рукояти,
    // l — левая поддержка. Пистолет держат двумя руками у самой рукояти.
    grips: { r: [-0.05, 0.0, 0], l: [0.12, 0.10, 0] },
  },
  rifle: {
    url: '/models/assault_rifle.glb',
    scale: 0.18,
    rotY: Math.PI / 2,
    offset: [0.02, -0.04, -0.10],
    muzzle: [3.247, 0.655, 0],
    tint: { Wood: 0x6b4a2b, DarkWood: 0x54381f, Black: 0x141518, Metal: 0x3a3f47, DarkMetal: 0x2a2e34 },
    // r — пистолетная рукоять (кисть у спусковой), l — цевьё.
    // ⚠️ y хвата обязан идти по ЛИНИИ СТВОЛА этой модели, а не по общему числу:
    // у винтовки ствол выше (дуло y=0.655), поэтому цевьё держим у y≈0.45 —
    // на y=0.05 (как у снайперки, где ствол ниже) кисть повисала на ~0.11 м
    // ниже оружия. Правую подняли к y=-0.10 и подали вперёд к x=0.20: на
    // [0.12,-0.30] кисть уходила на ~0.02 м ниже нижней кромки кадра (FOV 75)
    // и была не видна вовсе, хотя точка на рукояти верная (разбор чисел —
    // tools/gun-pack, воспроизводит трансформ scale·rotY·offset).
    grips: { r: [0.20, -0.10, 0], l: [2.05, 0.45, 0] },
  },
  sniper: {
    url: '/models/sniper.glb',
    scale: 0.155,
    rotY: Math.PI / 2,
    offset: [0.02, -0.05, -0.06],
    muzzle: [5.539, 0.292, 0],
    // Grey — приклад (даём оливковый, как AWP), Glass — линза прицела (синеватая).
    tint: { Black: 0x141518, DarkMetal: 0x2a2e34, LightMetal: 0x6a7078, Grey: 0x5a6144, Glass: 0x2a3a4a },
    grips: { r: [0.60, -0.10, 0], l: [2.30, 0.05, 0] },  // рукоять / форенд
  },
};

// ---- Руки вьюмодели ----
//
// На каждом стволе — ДВЕ руки (предплечье + кисть), держат его за две точки
// хвата. Крепятся к группе ствола `g`, поэтому сами собой наследуют отдачу,
// качание перезарядки, прижатие на бегу и прячутся вместе со стволом (в зуме
// снайперки и с гранатой в руке). Плечи заданы в системе `g` (в покое = система
// камеры): руки «растут» из нижних углов кадра к оружию.
//
// ⚠️ Это чисто вьюмодельные руки, НЕ то тело, что видит противник (`remote.js`).
// Своё настоящее тело от камеры спрятано (`body.js`, материал GHOST), эти руки —
// отдельная косметика первого лица.
const armSkin = new THREE.MeshStandardMaterial({ color: 0xba8b66, roughness: 0.85, metalness: 0.0 });
const armSleeve = new THREE.MeshStandardMaterial({ color: 0x39423a, roughness: 0.9, metalness: 0.05 });
const SHOULDER_R = new THREE.Vector3(0.30, -0.42, 0.32);
const SHOULDER_L = new THREE.Vector3(-0.30, -0.42, 0.32);
const _armUp = new THREE.Vector3(0, 1, 0);

/** Одна рука: предплечье-цилиндр от плеча к хвату + кисть-сфера на хвате.
 *  Локоть не моделируем — в кадре его всё равно закрывает оружие, а прямое
 *  предплечье из нижнего угла к рукояти читается как «держит». */
function makeViewArm(shoulder, grip) {
  const arm = new THREE.Group();
  const dir = new THREE.Vector3().subVectors(grip, shoulder);
  const len = Math.max(0.05, dir.length());
  const forearm = new THREE.Mesh(new THREE.CylinderGeometry(0.038, 0.055, len, 10), armSleeve);
  forearm.position.copy(shoulder).addScaledVector(dir, 0.5);
  forearm.quaternion.setFromUnitVectors(_armUp, dir.clone().normalize());
  forearm.frustumCulled = false;
  arm.add(forearm);
  const hand = new THREE.Mesh(new THREE.SphereGeometry(0.055, 10, 8), armSkin);
  hand.scale.set(1, 0.85, 1.25); hand.position.copy(grip); hand.frustumCulled = false;
  arm.add(hand);
  return arm;
}

const gltfLoader = new GLTFLoader();

// Масштаб вспышки по стволу: у пистолета скромнее, у снайперки крупный сноп.
const MUZZLE_SCALE = { pistol: 0.8, rifle: 1.0, sniper: 1.35 };

/** Дульная вспышка — добавляется синхронно: `tryShoot` читает `g.userData.flash`
 *  сразу при первом же выстреле, до того как успеет догрузиться GLB.
 *
 *  ⚠️ `g.userData.flash` — это ГРУППА-ЯКОРЬ в точке дула, а не сам меш. Её позицию
 *  читают и правят как «точку дула» (трассер в `tryShoot`, поджим у стены и у пола
 *  в `updateWeaponView`), поэтому она обязана оставаться пустым Object с позицией.
 *  Видимые слои — её дети; их гасит `updateFx` по спискам `flashParts`/`flashLight`.
 *
 *  Слои (все аддитивные и ярче 1.0, поэтому их подхватывает bloom):
 *    • две крест-накрест карточки — яркая «звезда» вспышки с торца ствола;
 *    • конус-язык пламени ВДОЛЬ ствола (вершина вперёд, −Z) — объём и «выброс огня»;
 *    • точечный свет у дула — единственное, что подсвечивает СЦЕНУ (стены, руки,
 *      ствол) в такт выстрелам; тени не отбрасывает (вспышка на 1–2 кадра). */
function addMuzzleFlash(g, z, scale = 1) {
  const flash = new THREE.Group();
  flash.position.set(0, 0.02, z);
  flash.scale.setScalar(scale);
  flash.userData.base = scale;                 // базовый масштаб; на выстреле поверх — jitter

  const cards = [];
  for (let i = 0; i < 2; i++) {
    const card = new THREE.Mesh(new THREE.PlaneGeometry(0.18, 0.18),
      new THREE.MeshBasicMaterial({
        color: 0xffe6a0, transparent: true, opacity: 0,
        blending: THREE.AdditiveBlending, depthWrite: false,
      }));
    flash.add(card); cards.push(card);
  }

  // Конус: ось по умолчанию +Y; поворот x=−π/2 уводит вершину в −Z (от игрока),
  // основание остаётся у дула. Открытый (без донца) — так это плюмаж, а не сосулька.
  const cone = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.275, 8, 1, true),
    new THREE.MeshBasicMaterial({
      color: 0xffb04a, transparent: true, opacity: 0,
      blending: THREE.AdditiveBlending, depthWrite: false,
    }));
  cone.rotation.x = -Math.PI / 2; cone.position.z = -0.14;
  flash.add(cone);

  const light = new THREE.PointLight(0xffcaa0, 0, 9, 2);
  flash.add(light);

  g.add(flash);
  g.userData.flash = flash;
  g.userData.flashCards = cards;               // крутим по z на каждом выстреле
  g.userData.flashParts = [...cards, cone];    // гасим прозрачность в updateFx
  g.userData.flashLight = light;
  return flash;
}

function buildPrimitiveGun(type, g) {
  if (type === 'pistol') {
    g.add(new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.13, 0.24), metalDark));
    const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.022, 0.22, 8), metalBlack);
    bar.rotation.x = Math.PI / 2; bar.position.set(0, 0.03, -0.2); g.add(bar);
  } else if (type === 'rifle') {
    g.add(new THREE.Mesh(new THREE.BoxGeometry(0.11, 0.15, 0.48), metalDark));
    const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.028, 0.4, 8), metalBlack);
    bar.rotation.x = Math.PI / 2; bar.position.set(0, 0.02, -0.42); g.add(bar);
  } else {
    g.add(new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.14, 0.7), metalDark));
    const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.5, 8), metalBlack);
    bar.rotation.x = Math.PI / 2; bar.position.set(0, 0.02, -0.6); g.add(bar);
    const sc = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.2, 10), metalBlack);
    sc.rotation.x = Math.PI / 2; sc.position.set(0, 0.11, -0.05); g.add(sc);
  }
}

/** Догружает GLB в уже существующую группу ствола и переставляет вспышку в
 *  реальную точку дула. Не блокирует: до загрузки в руках примитив (если задан
 *  фолбэк) либо просто вспышка; при ошибке загрузки — примитив. */
function loadGunModel(type, g, flash) {
  const m = GUN_MODELS[type];
  gltfLoader.load(m.url, (gltf) => {
    const model = gltf.scene;
    model.scale.setScalar(m.scale);
    model.rotation.set(0, m.rotY, 0);
    // Центрируем по bounding box (уже с учётом масштаба и поворота), потом сдвиг.
    const box = new THREE.Box3().setFromObject(model);
    const c = box.getCenter(new THREE.Vector3());
    model.position.set(-c.x + m.offset[0], -c.y + m.offset[1], -c.z + m.offset[2]);
    // Перекраска частей + отключаем frustum culling: вьюмодель у самой камеры,
    // при наклоне её bounding sphere уезжает за край и Three её ошибочно срезал бы.
    model.traverse((o) => {
      if (!o.isMesh) return;
      o.frustumCulled = false;
      const name = o.material && o.material.name;
      if (m.tint && name in m.tint) {
        o.material = o.material.clone();
        o.material.color.setHex(m.tint[name]);
        o.material.metalness = 0.35; o.material.roughness = 0.6;
      }
    });
    g.add(model);
    // Точка дула и точки хвата — ТОЧНО из матрицы модели (scale·rotY·offset),
    // а не на глаз: та же матрица, что переводит модель в систему группы g.
    model.updateMatrix();
    flash.position.copy(new THREE.Vector3(...m.muzzle).applyMatrix4(model.matrix));
    if (m.grips) {
      const gr = new THREE.Vector3(...m.grips.r).applyMatrix4(model.matrix);
      const gl = new THREE.Vector3(...m.grips.l).applyMatrix4(model.matrix);
      g.add(makeViewArm(SHOULDER_R, gr));
      g.add(makeViewArm(SHOULDER_L, gl));
    }
  }, undefined, (err) => {
    console.error('Не загрузилась модель оружия', type, err);
    buildPrimitiveGun(type, g);   // фолбэк: в руках примитив, вспышка уже на месте
  });
}

function buildGun(type) {
  const g = new THREE.Group();
  const sc = MUZZLE_SCALE[type] ?? 1;
  if (GUN_MODELS[type]) {
    // Провизорная позиция дула на первые кадры, пока грузится GLB.
    const flash = addMuzzleFlash(g, type === 'sniper' ? -0.86 : type === 'rifle' ? -0.66 : -0.34, sc);
    loadGunModel(type, g, flash);
  } else {
    buildPrimitiveGun(type, g);
    addMuzzleFlash(g, type === 'sniper' ? -0.86 : type === 'rifle' ? -0.66 : -0.34, sc);
  }
  return g;
}

export const guns = { pistol: buildGun('pistol'), rifle: buildGun('rifle'), sniper: buildGun('sniper') };
Object.values(guns).forEach(g => { g.visible = false; gunHolder.add(g); });
const GUN_HOME = new THREE.Vector3(0.22, -0.20, -0.45);
const GUN_ADS = new THREE.Vector3(0.0, -0.13, -0.34);
// Бег: ствол уходит вниз-вправо и разворачивается поперёк корпуса — то же,
// что делает человек, который бежит, а не целится. Поворот отрицательный по X
// опускает дуло, положительный по Y уводит его влево через грудь.
//
// ⚠️ Цифры подобраны РАСЧЁТОМ, а не на глаз (тот же приём, что у хвата
// в `remote.js`): при FOV 75 и 16:9 дуло винтовки садится в точку экрана
// (−0.08, −0.65) в нормированных координатах, то есть в нижнюю треть кадра,
// а рукоять уходит за нижний правый край. Опусти ещё на десятую — оружие
// пропадёт из кадра целиком, и «прижал к корпусу» будет неотличимо от
// «оружие исчезло».
const GUN_SPRINT = new THREE.Vector3(0.26, -0.26, -0.34);
const SPRINT_ROT = [-0.35, 0.55, 0.6];

// ---- Позиция вьюмодели: база отдельно от поджимов ----
//
// ⚠️ Баг-урок «дребезг оружия у боковой стены». Поджимы (у стены, у пола) НЕЛЬЗЯ
// писать прямо в `gunHolder.position`: анимация следующего кадра стартует лерпом
// ОТ УЖЕ ПОДЖАТОЙ точки, дуло на кадр выходит из стены, поджим пропадает, лерп
// тянет ствол обратно в стену — и так каждый кадр. Автоколебание с периодом в
// пару кадров и амплитудой во весь поджим; вживую читается как «оружие трясётся,
// когда ведёшь его в стену сбоку».
//
// Поэтому анимации (дом/ADS/перезарядка/бег) пишут в `gunBase` — это позиция БЕЗ
// поджимов, — а `gunHolder.position` каждый кадр пересобирается: копия базы плюс
// поджимы. Обратной связи нет: поджим считается по базовой позе, а не по своему
// же результату.
const gunBase = new THREE.Vector3().copy(GUN_HOME);
const WALL_CLR = 0.12;   // зазор между дулом и стеной, м
let wallPull = 0;        // сглаженная величина поджима у стены, м

// ---- Бег: оружие прижато к корпусу ----
//
// Пока игрок бежит, прицеливания нет и выстрела нет. Остановился — оружие
// вскидывается за `SPRINT_RAISE`, и только после этого снова стреляет.
//
// ⚠️ Это НЕ косметика: ровно то же правило считает сервер
// (`movement::is_sprinting` + `movement::step_stow` + `room::fire`). Клиент
// показывает его заранее, чтобы выстрел не «пропадал» молча, — но решает
// всё равно сервер.
//
// ⚠️ Обе константы — ЗЕРКАЛО `shared/src/movement.rs::SPRINT_STOW/SPRINT_RAISE`,
// менять только парой. Сервер считает тот же вес по тем же кадрам ввода,
// поэтому «когда снова можно стрелять» у него и у нас совпадает точно —
// без запасов на пинг и джиттер (разбор — в доке `step_stow`).
export const SPRINT_RAISE = 0.25;   // с — вскидывание после остановки
const SPRINT_STOW = 0.15;           // с — прижатие к корпусу на старте бега

/** Готово ли оружие стрелять и целиться (на бегу и пока вскидывается — нет).
 *
 *  ⚠️ Правило и картинка — ОДНА величина `S.stow`, а не таймер рядом с позой.
 *  Иначе они расходятся ровно там, где это заметнее всего: короткое касание
 *  Shift опускало бы ствол на волосок, а запрет держало бы полные 0.25 с. */
export const weaponReady = () => !S.sprinting && S.stow <= 0;
guns[S.curKey].visible = true;
document.getElementById('max').textContent = W().mag;

export function switchWeapon(key) {
  if (!WEAPONS[key]) return;
  // ⚠️ Отмена гранаты идёт ДО проверки «этот ствол уже в руках». С гранатой в руке
  // S.curKey остаётся прежним стволом, поэтому самый естественный жест отмены —
  // нажать цифру СВОЕГО же оружия — упирался в ранний return: замах не снимался,
  // и на отпускании ЛКМ граната всё равно улетала. Ствол при этом возвращается сам:
  // его видимость выводится из !S.nadeMode в updateWeaponView.
  if (S.nadeMode) { exitNade(); playSwitch(); }
  if (key === S.curKey) return;
  // Сброс очереди одиночного выстрела: иначе стойкий флаг тратит патрон фантомным
  // выстрелом нового ствола, запускает кулдаун rof — и первый реальный клик «не стреляет».
  S.semiQueued = false;
  if (S.reloading) cancelReload();             // смена оружия отменяет перезарядку (как в шутерах)
  guns[S.curKey].visible = false;
  S.curKey = key; guns[S.curKey].visible = true;
  document.querySelectorAll('#weapons .w').forEach(el =>
    el.classList.toggle('active', el.dataset.w === key));
  document.getElementById('max').textContent = W().mag;
  sendSwitch(WEAPON_ID[key]);       // сервер тоже должен знать, что в руках
  playSwitch();
}

/**
 * Убрать перезарядку — с экрана и из ушей.
 *
 * ⚠️ Звук гасится обязательно (`stopReload`): семпл длится почти всю
 * перезарядку, и оставленный доигрывать он врал бы о том, что руки всё ещё
 * в магазине. Своя перезарядка отменяется в двух случаях — сменой оружия
 * и бегом, — и оба обязаны звучать одинаково.
 */
export function cancelReload() {
  S.reloading = false;
  stopReload();
  document.getElementById('reloading').style.visibility = 'hidden';
  document.getElementById('rbar').style.visibility = 'hidden';
}

// Короткая подсказка на месте надписи «ПЕРЕЗАРЯДКА…».
//
// Нужна потому, что запрет молчит: игрок жмёт R на бегу — и не происходит
// НИЧЕГО (ни звука, ни полоски, ни кадра на сервер). Без объяснения это
// читается как «клавиша залипла», а не как правило игры.
const RELOAD_LABEL = 'ПЕРЕЗАРЯДКА…';
let hintTimer = 0;
function reloadHint(text) {
  const el = document.getElementById('reloading');
  el.textContent = text;
  el.style.visibility = 'visible';
  clearTimeout(hintTimer);
  hintTimer = setTimeout(() => {
    el.textContent = RELOAD_LABEL;
    if (!S.reloading) el.style.visibility = 'hidden';
  }, 800);
}

export function startReload() {
  if (S.nadeMode) return;
  // На бегу перезарядки не бывает — то же правило считает сервер
  // (`room::reload`). Проверяем ЗДЕСЬ, до звука и до кадра: иначе игрок
  // услышал бы лязг магазина, которого на сервере не случилось, и увидел
  // бы полоску, которая ничем не кончится.
  if (S.sprinting) { reloadHint('НА БЕГУ НЕЛЬЗЯ'); return; }
  const w = W(); if (S.reloading || w.ammo === w.mag) return;
  S.reloading = true; S.reloadEnd = clock.elapsedTime + w.reload;
  sendReload();
  document.getElementById('reloading').style.visibility = 'visible';
  document.getElementById('rbar').style.visibility = 'visible';
  playReload();
}

const raycaster = new THREE.Raycaster();
// ⚠️ Камера здесь — страховка на будущее, а не украшение. Рекурсивный луч по
// группе, где есть спрайты (ник, полоска HP), Three.js без `raycaster.camera`
// не считает вовсе: он бросает исключение ВНУТРИ intersectObjects, ещё до
// любого фильтра `!isSprite`. Однажды это уронило tryShoot до вспышки, декали
// и трассера — урон шёл, а весь визуал выстрела пропадал (баг-урок в
// md/netcode.md). Сам луч по чужим телам переехал в `remote.js::bodiesAlong`
// (там та же строка), здесь остался только рейкаст по стенам.
raycaster.camera = camera;

const _dir = new THREE.Vector3();
const _org = new THREE.Vector3();
// Рабочие для поджима у пола: мировая точка дула и подъём холдера.
const _mw = new THREE.Vector3();
const _lift = new THREE.Vector3();
const _q = new THREE.Quaternion();
// Рабочие для поджима у стены: отрезок глаз→дуло в мире и он же в системе камеры.
const _mdir = new THREE.Vector3();
const _ldir = new THREE.Vector3();

export function tryShoot() {
  const t = clock.elapsedTime, w = W();
  // На бегу оружие прижато к корпусу. Патрон не тратим и выстрел не рисуем:
  // сервер такой кадр всё равно отвергнет (`room.rs::fire`), а нарисованный
  // выстрел без урона читается как «попадания не засчитываются».
  if (!weaponReady()) return;
  if (S.reloading || t - w.lastShot < w.rof) return;
  if (w.ammo <= 0) { startReload(); return; }
  w.lastShot = t;
  // Патрон списываем сразу (предсказание), сервер поправит кадром OP_AMMO.
  // Ждать ответа нельзя: при пинге 250 мс счётчик отставал бы на четверть секунды.
  w.ammo--;

  const wid = WEAPON_ID[S.curKey];
  // Стреляем туда, куда РЕАЛЬНО смотрит камера — то есть с учётом отдачи.
  // Иначе выстрел уходил бы не туда, куда игрок целится глазами.
  const pitch = Math.max(-1.6, Math.min(1.6, S.pitch + S.recoil));

  // Разброс детерминирован номером выстрела: сервер посчитает такой же луч
  const seq = sendFire(S.yaw, pitch, wid, S.aiming, S.interpDelay);
  const moveP = Math.sqrt(player.vel.x * player.vel.x + player.vel.z * player.vel.z) > 1;
  const [sx, sy] = seq ? spreadOffset(seq ^ (me.id << 16)) : [0, 0];
  const scale = w.spread * spreadScale(S.aiming, moveP);
  aimDir(S.yaw + sx * scale, Math.max(-1.6, Math.min(1.6, pitch + sy * scale)), _dir);

  // Луч пускаем из позиции глаз, как сервер, а не из камеры: у камеры есть
  // покачивание и тряска, которых на сервере нет. А вот боковой сдвиг наклона
  // (S.leanShift) на сервере ЕСТЬ — он стреляет из наклонённой точки
  // (hit::lean_point). Без этого сдвига при выглядывании из-за угла камера и
  // перекрестье уезжают вбок и целятся мимо угла, а локальный луч бьёт из-за
  // угла в стену: сервер попадает, а клиент рисует «пулю в стене» (декаль +
  // конец трассера). Зеркало сервера: hit::lean_point / movement::lean_shift.
  _org.copy(player.pos);
  _org.x += Math.cos(S.yaw) * S.leanShift;
  _org.z += -Math.sin(S.yaw) * S.leanShift;
  raycaster.set(_org, _dir);
  raycaster.far = w.range || 130;
  // Луч пускаем по геометрии карты. Урон по людям И по ботам по-прежнему
  // считает сервер (кадр OP_HIT) — клиент им ничего не решает.
  const wallHit = raycaster.intersectObjects(wallMeshes, false)[0];

  const origin = new THREE.Vector3(); guns[S.curKey].userData.flash.getWorldPosition(origin);
  // ⚠️ Пуля ПРОШИВАЕТ людей (протокол 19): тело её больше не останавливает,
  // останавливает только геометрия карты. Поэтому конец луча — всегда стена
  // (или предел дальности), и дырка ставится ВСЕГДА, в том числе за спиной
  // пробитого. Раньше здесь была обратная логика: тело перед стеной гасило
  // декаль — это чинило старую жалобу «дырка за врагом», а теперь дырка за
  // врагом и есть то, что владелец просит показывать.
  let end, endNrm = null;
  if (wallHit) {
    const h = wallHit;
    end = h.point.clone();
    const nrm = h.face
      ? h.face.normal.clone().transformDirection(h.object.matrixWorld)
      : new THREE.Vector3(0, 1, 0);
    endNrm = nrm;
    spawnSparks(h.point, nrm, 0xffcc66, 8); addDecal(h.point, nrm);
  } else {
    // ⚠️ Конец промаха считаем от ГЛАЗ, а не от дула: дуло на 0.18 м ниже
    // камеры, и луч от него шёл параллельно линии прицела — трассер уходил
    // вниз-вправо и не пересекал перекрестье никогда.
    end = _org.clone().addScaledVector(_dir, w.range || 130);
  }
  // Брызги на каждом, кого пуля прошла по дороге. Луч свой, от ГЛАЗ до конца
  // (не от дула): урон всё равно считает сервер, это чистая косметика.
  // Стоял человек вплотную к стене — на ней ещё и остаётся пятно крови
  // (порог расстояния и проверку держит сам `spawnBloodSplat`).
  for (const p of bodiesAlong(_org, end)) {
    spawnSparks(p, _dir.clone().negate(), 0x991111, 6);
    spawnBloodSplat(p, endNrm ? end : null, endNrm);
  }
  spawnTracer(origin, end, wid, true);
  // Дульная вспышка: карточки и конус вспыхивают, свет бьёт в сцену, форма
  // не повторяется (случайный поворот карточек + jitter масштаба всей группы).
  const u = guns[S.curKey].userData;
  u.flash.scale.setScalar(u.flash.userData.base * (0.85 + Math.random() * 0.3));
  for (const p of u.flashParts) p.material.opacity = 0.5;
  for (const c of u.flashCards) c.rotation.z = Math.random() * Math.PI;
  u.flashLight.intensity = 5;        // пик; гаснет за пару кадров в updateFx
  spawnMuzzleSmoke(origin, _dir);    // лёгкий дымок из ствола (в мировых координатах)
  // ⚠️ Отдача в прицеле масштабируется зумом. Она задана В УГЛЕ, а на экране
  // угол виден тем крупнее, чем уже FOV: 0.060 рад снайперки при adsFov=18
  // подбрасывают картинку на 1/5 высоты экрана (замерено: 133 px из 705).
  // Игрок читает это не как отдачу, а как «ствол бьёт ниже перекрестья» —
  // пуля уходит точно в прицел, но через кадр камера уезжает вверх от дырки.
  // Множитель adsFov/BASE_FOV делает подброс В ПИКСЕЛЯХ примерно одинаковым
  // для всех стволов. Крутить ощущение отдачи — здесь и в `recoil` у WEAPONS.
  const kick = w.recoil * (S.aiming ? w.adsFov / BASE_FOV : 1);
  S.recoil = Math.min(S.recoil + kick, 0.12);
  S.shake = Math.min(S.shake + kick * 1.5, 0.08);
  guns[S.curKey].position.z += w.recoil * 3;
  playShot();
}

// Вьюмодель: ADS/FOV, прицел снайперки, анимация перезарядки, поджим у стены.
export function updateWeaponView(dt, mv) {
  // ---- Бег: считаем состояние оружия ----
  //
  // Признак берём из `mv`, то есть из результата `stepPhysics` — это уже ФАКТ
  // бега, посчитанный той же формулой, что на сервере (`movement::step`):
  // Shift + реальное движение + хватило выносливости. Считать его отдельно
  // по клавишам нельзя — разъедется сразу в двух случаях: «W и S зажаты
  // вместе» (игрок стоит) и «силы кончились» (игрок идёт шагом).
  S.sprinting = mv.sprint;
  // Порт `movement.rs::step_stow` — тот же вес, те же константы. Сервер
  // считает его по кадрам ввода, мы по кадрам рендера; расходиться им не на
  // чем, потому что обе стороны считают одно и то же прошедшее время.
  S.stow = S.sprinting
    ? Math.min(1, S.stow + dt / SPRINT_STOW)
    : Math.max(0, S.stow - dt / SPRINT_RAISE);
  S.aiming = S.ads && weaponReady();

  // ---- Бег обрывает перезарядку ----
  //
  // Правило владельца: бежать с руками в магазине нельзя. Сервер делает то же
  // самое на кадре ввода (`room::apply_input`) и присылает кадр OP_AMMO
  // с погашенным флагом; здесь мы просто не ждём полпинга, чтобы это увидеть.
  // Патроны НЕ пополняются — прерванная перезарядка потеряна, иначе бег стал
  // бы способом её ускорить.
  if (S.sprinting && S.reloading) { cancelReload(); reloadHint('ПЕРЕЗАРЯДКА ПРЕРВАНА'); }

  // ---- Граната в руке тоже обрывает перезарядку ----
  //
  // Тот же принцип, что у бега: руки заняты — магазин не меняют. Без этого
  // граната работала бы «сейфом» для перезарядки (жалоба владельца): начал
  // перезарядку, достал гранату, вернулся к стволу — и магазин полон, хотя
  // перезарядку игрок не досидел. Патроны НЕ пополняются, как и при беге.
  // Сервер делает то же самое (`room::update` по биту BTN_NADE + `room::reload`).
  //
  // ⚠️ Проверка стоит ЗДЕСЬ, а не в `grenades.js::equipGrenade`: weapons.js уже
  // импортирует `exitNade` оттуда, и обратный импорт `cancelReload` замкнул бы
  // цикл модулей — тот самый класс TDZ-ошибок, который не ловят ни линт, ни
  // сборка, ни тесты (только headless-smoke). Кадр рендера опаздывает от
  // нажатия максимум на 16 мс, а покрывает разом все пути входа в режим
  // гранаты: клавиши 4–7, тач-кнопку и смену типа гранаты на лету.
  if (S.nadeMode && S.reloading) { cancelReload(); reloadHint('ПЕРЕЗАРЯДКА ПРЕРВАНА'); }

  const wantFov = S.aiming ? W().adsFov : BASE_FOV + (S.sprinting ? 6 : 0);
  camera.fov += (wantFov - camera.fov) * Math.min(dt * 12, 1);
  camera.updateProjectionMatrix();

  const scoped = S.aiming && W().scope && camera.fov < 45 && !S.nadeMode;
  document.getElementById('scope').classList.toggle('on', scoped);
  // прячем ствол в снайп-прицеле и когда в руке граната
  guns[S.curKey].visible = !scoped && !S.nadeMode;
  guns[S.curKey].position.lerp(new THREE.Vector3(0, 0, 0), Math.min(dt * 10, 1)); // возврат отдачи

  if (S.reloading) {
    // прогресс 0→1; ствол опускается и качается, в конце возвращается
    const p = 1 - Math.max(0, (S.reloadEnd - clock.elapsedTime) / W().reload);
    const dip = Math.sin(p * Math.PI);
    gunBase.set(GUN_HOME.x, GUN_HOME.y - dip * 0.22, GUN_HOME.z + dip * 0.06);
    gunHolder.rotation.set(dip * 0.9, 0, dip * 0.5);
  } else {
    gunBase.lerp(S.aiming ? GUN_ADS : GUN_HOME, Math.min(dt * 15, 1));
    gunHolder.rotation.x += (0 - gunHolder.rotation.x) * Math.min(dt * 14, 1);
    gunHolder.rotation.y += (0 - gunHolder.rotation.y) * Math.min(dt * 14, 1);
    gunHolder.rotation.z += (0 - gunHolder.rotation.z) * Math.min(dt * 14, 1);
  }

  // Прижатие к корпусу на бегу — ПОСЛЕ обеих веток, по той же причине, что
  // и поджим у стены: это не анимация, а положение оружия. Перезарядка на
  // бегу должна качать уже прижатый ствол, а не тянуть его обратно к плечу.
  // Вес `S.stow` доводит до цели сам, поэтому здесь не lerp по времени.
  if (S.stow > 0.001) {
    gunBase.lerp(GUN_SPRINT, S.stow);
    gunHolder.rotation.x += (SPRINT_ROT[0] - gunHolder.rotation.x) * S.stow;
    gunHolder.rotation.y += (SPRINT_ROT[1] - gunHolder.rotation.y) * S.stow;
    gunHolder.rotation.z += (SPRINT_ROT[2] - gunHolder.rotation.z) * S.stow;
  }

  // ---- Поджим у стены — ПОСЛЕ обеих веток ----
  //
  // Иначе во время перезарядки ствол лезет сквозь стену (баг-урок: раньше поджим
  // стоял внутри else и на перезарядке просто выключался).
  //
  // ⚠️ Дуло не должно уходить ЗА стену. Прежняя версия пускала луч ОТ ГЛАЗ СТРОГО
  // ВПЕРЁД и двигала холдер только по z — и не ловила БОКОВОЙ контакт: вьюмодель
  // смещена на 0.22 м ВПРАВО от камеры (`GUN_HOME.x`), поэтому вдоль стены СПРАВА
  // (и при наклоне E в неё) дуло пробивало стену сбоку, а прямой луч её не видел
  // вовсе. Слева того же не было — оружие смещено в другую сторону. Теперь луч
  // идёт ОТ ГЛАЗ К РЕАЛЬНОЙ ТОЧКЕ ДУЛА: если стена пересекает отрезок глаз→дуло,
  // уводим весь холдер НАЗАД ВДОЛЬ этого отрезка так, чтобы дуло встало перед
  // стеной с зазором. Ход назад вдоль отрезка убирает и боковую составляющую,
  // поэтому работает симметрично для обеих сторон и в любой позе ствола.
  //
  // ⚠️ Порядок здесь принципиален (см. `gunBase` выше): сначала кладём в холдер
  // ЧИСТУЮ базовую позу, и только по ней меряем контакт со стеной. Мерить по
  // позе, в которую уже вписан поджим прошлого кадра, нельзя — это и есть та
  // петля обратной связи, от которой ствол дребезжал.
  gunHolder.position.copy(gunBase);

  const flashW = guns[S.curKey].userData.flash;
  camera.getWorldPosition(_wp);
  flashW.getWorldPosition(_mw);                 // мировая точка дула (поза БЕЗ поджимов)
  _mdir.subVectors(_mw, _wp);                   // отрезок глаз→дуло (мир)
  const muzzleDist = _mdir.length();
  let want = 0;
  if (muzzleDist > 1e-4) {
    _mdir.multiplyScalar(1 / muzzleDist);
    // Луч бьём ДАЛЬШЕ дула на зазор WALL_CLR. Иначе поджим включается скачком
    // сразу на весь зазор (стена ровно у дула ⇒ pull = 0 + зазор) и любое
    // касание даёт рывок на 12 см. С запасом функция поджима непрерывна:
    // растёт с нуля по мере приближения стены.
    // ⚠️ Луч свой (player.js::rayWalls по коробкам карты), а не three.js:
    // он пускается каждый кадр, а рейкаст по геометрии импортированной карты
    // стоит на порядок дороже — см. разбор в шапке world.js.
    const reach = muzzleDist + WALL_CLR;
    const wall = rayWalls(_wp.x, _wp.y, _wp.z, _mdir.x, _mdir.y, _mdir.z, reach);
    // Насколько увести дуло назад вдоль отрезка. Ограничиваем длиной отрезка,
    // чтобы вплотную к стене ствол не улетел за камеру.
    if (wall < reach) want = Math.min(Math.max(0, reach - wall), muzzleDist);
  }
  // Сглаживание по времени: убирает щелчок на кадре, когда луч только-только
  // задел угол бокса. Вход быстрее выхода — ствол обязан уходить из стены
  // сразу, а возвращаться может мягко.
  wallPull += (want - wallPull) * Math.min(dt * (want > wallPull ? 30 : 12), 1);
  if (wallPull > 1e-4 && muzzleDist > 1e-4) {
    // тот же вектор отрезка, но в системе камеры (родителя холдера): сдвиг
    // холдера задаём в его координатах, поэтому переводим направление в них.
    _ldir.copy(_mdir).applyQuaternion(camera.getWorldQuaternion(_q).invert());
    gunHolder.position.addScaledVector(_ldir, -wallPull);
  }

  // ---- Поджим у ПОЛА ----
  //
  // ⚠️ ПОЛА В `wallMeshes` НЕТ — это отдельная плоскость (`scene.js`), луч выше
  // её не видит. Отсюда жалоба владельца «лёжа снайперка уходит стволом под
  // пол»: лёжа глаза на 0.45 м, а длинный ствол свисает ниже линии взгляда.
  //
  // Прежняя версия мерила запас лучом ОТ КАМЕРЫ до плоскости y=0 и по нему
  // ограничивала z. Но дуло висит ниже линии взгляда (`GUN_HOME.y=-0.20`, а на
  // перезарядке ещё ниже и довёрнуто вниз), поэтому «луч от глаз» давал запас
  // больше реального — и при взгляде вниз лёжа дуло всё равно тонуло, а при
  // горизонтальной камере (ствол опущен перезарядкой) поджим не срабатывал
  // вовсе. Теперь считаем по РЕАЛЬНОЙ мировой точке дула: если она под полом —
  // поднимаем весь холдер ровно настолько, чтобы дуло встало на пол. Подъём
  // задан мировым вектором «вверх», переведённым в систему камеры (родителя
  // холдера), поэтому одинаково работает под любым углом взгляда и позой ствола.
  //
  // Петли обратной связи здесь тоже нет (хотя подъём пишется прямо в холдер):
  // холдер каждый кадр пересобирается из `gunBase`, поэтому подъём не копится и
  // не влияет на замер следующего кадра.
  flashW.getWorldPosition(_mw);                 // мировая точка дула уже ПОСЛЕ поджима у стены
  const FLOOR_CLR = 0.05;
  if (_mw.y < FLOOR_CLR) {
    _lift.set(0, FLOOR_CLR - _mw.y, 0);
    _lift.applyQuaternion(camera.getWorldQuaternion(_q).invert());
    gunHolder.position.add(_lift);
  }

  if (S.reloading && clock.elapsedTime >= S.reloadEnd) {
    W().ammo = W().mag;
    cancelReload();   // звук к этому моменту уже кончился — семпл короче перезарядки
  }
}
